"use client"

import { OptimizedMediaType } from "@/app/types/media";
import { Box, CircularProgress, colors, IconButton, Modal, Typography } from "@mui/material";
import FlexBox from "@v2/components/styled/FlexBox";
import type { Swiper as SwiperType } from 'swiper';
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { animated, useSpring } from '@react-spring/web';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PAGINATION_HEIGHT = 'calc(56px + env(safe-area-inset-bottom, 0px))';

// The image keeps gliding after the finger leaves. A critically damped spring
// (friction = 2 * sqrt(tension)) coasts to a stop without bouncing back.
const FLING_TENSION = 30;
// The throw is measured over the last stretch of the finger path instead of the
// last frame, so the glide keeps going the way the finger was actually heading.
const FLING_SAMPLE_WINDOW = 120;
const FLING_FRICTION = 2 * Math.sqrt(FLING_TENSION);
// react-spring runs the spring in px/ms, so 1 / omega is how far that spring
// travels for every px/ms of throw speed.
const FLING_DISTANCE_PER_VELOCITY = 1 / (Math.sqrt(FLING_TENSION) * 1e-3);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SpringDiv = animated.div as React.ComponentType<React.PropsWithChildren<{ style?: any }>>;
import { useGesture } from '@use-gesture/react';

import { generateMediaSrc } from "@v2/components/MediaV2";
import { X } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";

function GallerySlide({ img, swiperRef, onZoomChange, isSwipingRef }: {
    img: OptimizedMediaType,
    swiperRef: React.RefObject<SwiperType | null>,
    onZoomChange: (zoomed: boolean) => void,
    isSwipingRef: React.RefObject<boolean>,
}) {
    const [isLoaded, setIsLoaded] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // last stretch of the finger path, used to aim the throw
    const pathRef = useRef<{ mx: number, my: number, t: number }[]>([]);

    // double-tap tracking
    const tapTimeRef = useRef(0);
    const tapPosRef = useRef({ x: 0, y: 0 });
    const activeTouchCountRef = useRef(0);

    const [{ scale, x, y }, api] = useSpring(() => ({
        scale: 1, x: 0, y: 0,
        config: { tension: 300, friction: 30 },
    }));

    const notifyZoom = useCallback((s: number) => {
        const zoomed = s > 1;
        onZoomChange(zoomed);
        if (swiperRef.current) swiperRef.current.allowTouchMove = !zoomed;
    }, [onZoomChange, swiperRef]);

    const resetZoom = useCallback(() => {
        api.start({ scale: 1, x: 0, y: 0 });
        notifyZoom(1);
    }, [api, notifyZoom]);

    useGesture(
        {
            onPinch: ({ origin: [ox, oy], first, movement: [ms], memo, last }) => {
                if (isSwipingRef.current) return;
                if (first && swiperRef.current) swiperRef.current.allowTouchMove = false;
                const rect = containerRef.current!.getBoundingClientRect();

                const m = first ? {
                    dx: ox - (rect.left + rect.width / 2),
                    dy: oy - (rect.top + rect.height / 2),
                    x0: x.get(),
                    y0: y.get(),
                    s0: scale.get(),
                } : memo;

                const rawScale = m.s0 * ms;

                // rubber-band: past limits, follow with 15% resistance
                const stretchScale = rawScale > 10
                    ? 10 + (rawScale - 10) * 0.15
                    : rawScale < 1
                        ? 1 - (1 - rawScale) * 0.15
                        : rawScale;

                const displayScale = last ? Math.min(Math.max(rawScale, 1), 10) : stretchScale;
                const ratio = displayScale / m.s0;
                const rawX = m.dx * (1 - ratio) + m.x0 * ratio;
                const rawY = m.dy * (1 - ratio) + m.y0 * ratio;

                const imgW = rect.width;
                const imgH = imgW / 1.618;
                const maxAbsX = Math.max(0, (imgW * displayScale - rect.width) / 2);
                const maxAbsY = Math.max(0, (imgH * displayScale - rect.height) / 2);

                if (last) {
                    if (rawScale < 1.1) {
                        resetZoom();
                    } else {
                        const finalScale = Math.min(Math.max(rawScale, 1), 10);
                        const fMaxX = Math.max(0, (imgW * finalScale - rect.width) / 2);
                        const fMaxY = Math.max(0, (imgH * finalScale - rect.height) / 2);
                        const finalRatio = finalScale / m.s0;
                        const finalX = m.dx * (1 - finalRatio) + m.x0 * finalRatio;
                        const finalY = m.dy * (1 - finalRatio) + m.y0 * finalRatio;
                        api.start({
                            scale: finalScale,
                            x: Math.min(fMaxX, Math.max(-fMaxX, finalX)),
                            y: Math.min(fMaxY, Math.max(-fMaxY, finalY)),
                            config: { tension: 400, friction: 30 },
                        });
                        notifyZoom(finalScale);
                    }
                } else {
                    api.start({
                        scale: displayScale,
                        x: Math.min(maxAbsX, Math.max(-maxAbsX, rawX)),
                        y: Math.min(maxAbsY, Math.max(-maxAbsY, rawY)),
                        immediate: true,
                    });
                }

                return m;
            },
            onDrag: ({ movement: [mx, my], velocity: [vx, vy], direction: [dx, dy], first, last, memo, touches }) => {
                if (first) {
                    if (scale.get() <= 1 || touches > 1) return null;
                    tapTimeRef.current = 0; // pan started — cancel any pending double-tap
                    const x0 = x.get();
                    const y0 = y.get();
                    pathRef.current = [];
                    api.start({ x: x0, y: y0, immediate: true });
                    return { x0, y0 };
                }
                if (!memo || touches > 1) return memo;

                const now = performance.now();
                const path = pathRef.current;
                path.push({ mx, my, t: now });
                // holding still drops every older sample, so a pause before lifting throws nothing
                while (path.length > 1 && now - path[0].t > FLING_SAMPLE_WINDOW) path.shift();

                const s = scale.get();
                const rect = containerRef.current!.getBoundingClientRect();
                const imgW = rect.width;
                const imgH = imgW / 1.618;

                const maxAbsX = Math.max(0, (imgW * s - rect.width) / 2);
                const maxAbsY = Math.max(0, (imgH * s - rect.height) / 2);

                const stretch = (v: number, limit: number) => {
                    if (Math.abs(v) <= limit) return v;
                    const sign = v > 0 ? 1 : -1;
                    return sign * (limit + (Math.abs(v) - limit) * 0.25);
                };

                if (last) {
                    // average speed along the recent finger path (px/ms), so the glide
                    // leaves in the direction the finger was really travelling
                    const [oldest] = path;
                    const span = now - oldest.t;
                    const sampled = span >= 16;
                    const throwX = sampled ? (mx - oldest.mx) / span : vx * dx;
                    const throwY = sampled ? (my - oldest.my) / span : vy * dy;

                    const restX = memo.x0 + mx;
                    const restY = memo.y0 + my;
                    const targetX = Math.min(maxAbsX, Math.max(-maxAbsX, restX + throwX * FLING_DISTANCE_PER_VELOCITY));
                    const targetY = Math.min(maxAbsY, Math.max(-maxAbsY, restY + throwY * FLING_DISTANCE_PER_VELOCITY));

                    api.start({
                        x: targetX,
                        y: targetY,
                        config: key => {
                            const isX = key === 'x';
                            const from = isX ? restX : restY;
                            const to = isX ? targetX : targetY;
                            const limit = isX ? maxAbsX : maxAbsY;
                            const throwSpeed = isX ? throwX : throwY;

                            // dragged past the edge — snap back instead of gliding
                            if (Math.abs(from) > limit) return { tension: 400, friction: 30 };

                            // an edge can cut the glide short, so slow the throw down to
                            // whatever still fits, otherwise the spring overshoots the edge
                            const maxSpeed = Math.abs(to - from) / FLING_DISTANCE_PER_VELOCITY;

                            return {
                                tension: FLING_TENSION,
                                friction: FLING_FRICTION,
                                precision: 0.1,
                                velocity: Math.sign(throwSpeed) * Math.min(Math.abs(throwSpeed), maxSpeed),
                            };
                        },
                    });
                    return memo;
                }

                api.start({
                    x: stretch(memo.x0 + mx, maxAbsX),
                    y: stretch(memo.y0 + my, maxAbsY),
                    immediate: true,
                });
                return memo;
            },
        },
        { target: containerRef, drag: { filterTaps: true } }
    );

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        activeTouchCountRef.current = e.touches.length;
        if (e.touches.length === 1) {
            tapPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
    }, []);

    const handleTouchEnd = useCallback((e: React.TouchEvent) => {
        if (activeTouchCountRef.current !== 1) return;

        const changed = e.changedTouches[0];
        const moved = Math.hypot(
            changed.clientX - tapPosRef.current.x,
            changed.clientY - tapPosRef.current.y
        );
        if (moved > 10) return;

        const now = Date.now();
        if (now - tapTimeRef.current < 300) {
            tapTimeRef.current = 0;

            if (scale.get() > 1) {
                resetZoom();
            } else {
                const rect = containerRef.current!.getBoundingClientRect();
                const dx = changed.clientX - (rect.left + rect.width / 2);
                const dy = changed.clientY - (rect.top + rect.height / 2);
                const target = 3;
                const imgW = rect.width;
                const imgH = imgW / 1.618;
                const maxAbsX = Math.max(0, (imgW * target - rect.width) / 2);
                const maxAbsY = Math.max(0, (imgH * target - rect.height) / 2);
                api.start({
                    scale: target,
                    x: Math.min(maxAbsX, Math.max(-maxAbsX, dx * (1 - target))),
                    y: Math.min(maxAbsY, Math.max(-maxAbsY, dy * (1 - target))),
                });
                notifyZoom(target);
            }
        } else {
            tapTimeRef.current = now;
        }
    }, [api, scale, resetZoom, notifyZoom]);

    return (
        <div
            ref={containerRef}
            style={{
                width: '100%',
                height: `calc(100% - ${PAGINATION_HEIGHT} - env(safe-area-inset-top, 0px) - 48px)`,
                marginTop: 'env(safe-area-inset-top, 0px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', touchAction: 'none',
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <SpringDiv style={{ position: 'relative', width: '100vw', aspectRatio: '1.618', scale, x, y }}>
                <Image
                    onLoad={() => setIsLoaded(true)}
                    src={`${img.link}?width=1920&height=1187&quality=92&fill=1`}
                    fill
                    alt={img.alt ?? ""}
                    unoptimized
                    style={{ objectFit: 'cover' }}
                />

                <Box sx={{
                    position: 'absolute', inset: 0,
                    opacity: isLoaded ? 0 : 1,
                    transition: 'opacity 0.35s ease',
                    pointerEvents: 'none',
                    bgcolor: 'silver',
                }}>
                    <Image
                        src={generateMediaSrc(img.id, { width: 780, height: 482 })}
                        fill alt="" unoptimized
                        style={{ objectFit: 'cover' }}
                    />
                </Box>

                {!isLoaded && (
                    <Box sx={{
                        position: 'absolute', zIndex: 4, right: 8, left: 8,
                        top: 'calc(100% + 8px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                        <Typography sx={{ fontSize: 9, color: '#fff' }}>
                            در حال آماده‌سازی نسخه باکیفیت تصویر ...
                        </Typography>
                        <CircularProgress size={18} sx={{ color: '#fff' }} />
                    </Box>
                )}
            </SpringDiv>
        </div>
    );
}

export default function PdpMobileFullscreenGallery({ images, show, onClose }: {
    images: OptimizedMediaType[],
    show: number | null,
    onClose: () => void
}) {
    const swiperRef = useRef<SwiperType>(null);
    const isZoomedRef = useRef(false);
    const isSwipingRef = useRef(false);

    const [dragY, setDragY] = useState(0);
    const dragYRef = useRef(0);
    const isDraggingDown = useRef(false);
    const touchStartRef = useRef({ x: 0, y: 0 });
    const directionRef = useRef<'horizontal' | 'vertical' | null>(null);
    const lastMoveRef = useRef({ y: 0, time: 0 });
    const velocityRef = useRef(0);
    const closeDurationRef = useRef(320);

    useEffect(() => {
        if (show !== null) {
            setDragY(0);
            dragYRef.current = 0;
            setTimeout(() => swiperRef.current?.slideTo(show, 150));
        }
    }, [show]);

    useEffect(() => {
        if (show !== null) {
            window.history.pushState({ modal: true }, "");
            const handlePopState = () => { onClose(); };
            window.addEventListener("popstate", handlePopState);
            return () => {
                window.removeEventListener("popstate", handlePopState);
                if (window.history.state?.modal) window.history.back();
            };
        }
    }, [show, onClose]);

    const handleZoomChange = useCallback((zoomed: boolean) => {
        isZoomedRef.current = zoomed;
    }, []);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        if (isZoomedRef.current || e.touches.length !== 1) return;
        touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        directionRef.current = null;
        isDraggingDown.current = false;
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (isZoomedRef.current || e.touches.length !== 1) return;
        const deltaX = e.touches[0].clientX - touchStartRef.current.x;
        const deltaY = e.touches[0].clientY - touchStartRef.current.y;

        if (!directionRef.current && (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8)) {
            directionRef.current = Math.abs(deltaY) > Math.abs(deltaX) ? 'vertical' : 'horizontal';
        }

        if (directionRef.current === 'vertical' && deltaY > 0) {
            isDraggingDown.current = true;
            dragYRef.current = deltaY;
            setDragY(deltaY);
            const now = Date.now();
            const dt = now - lastMoveRef.current.time;
            if (dt > 0 && dt < 100) {
                velocityRef.current = (e.touches[0].clientY - lastMoveRef.current.y) / dt;
            }
            lastMoveRef.current = { y: e.touches[0].clientY, time: now };
        }
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (!isDraggingDown.current) return;
        isDraggingDown.current = false;
        if (dragYRef.current > 120) {
            const remaining = window.innerHeight - dragYRef.current;
            const v = Math.max(velocityRef.current, 0.5);
            const ms = Math.min(Math.max(remaining / v, 80), 300);
            closeDurationRef.current = ms;
            setDragY(window.innerHeight);
            setTimeout(() => onClose(), ms + 16);
        } else {
            closeDurationRef.current = 300;
            dragYRef.current = 0;
            setDragY(0);
        }
    }, [onClose]);

    const opacity = Math.max(0.05, 1 - dragY / 700);

    return (
        <Modal
            open={show !== null}
            onClose={onClose}
            aria-labelledby="gallery"
            aria-describedby="product images gallery"
            slotProps={{ backdrop: { style: { opacity } } }}
        >
            <Box
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                sx={{
                    height: '100vh', bgcolor: '#000',
                    border: 'unset', display: 'flex',
                    flexDirection: 'column', outline: 'none',
                }}
                style={{
                    transform: `translateY(${dragY}px)`,
                    transition: isDraggingDown.current ? 'none' : `transform ${closeDurationRef.current}ms cubic-bezier(0.32,0,0.67,0)`,
                    opacity,
                }}
            >
                <FlexBox sx={{
                    p: 1, justifyContent: 'flex-start',
                    zIndex: 10, position: 'absolute',
                    top: 'env(safe-area-inset-top, 0px)',
                }}>
                    <IconButton onClick={onClose} sx={{ bgcolor: 'rgba(0,0,0,0.5)' }}>
                        <X color="#fff" size={24} />
                    </IconButton>
                </FlexBox>

                <Box dir="ltr" sx={{
                    userSelect: 'none', height: 1,
                    '& .swiper': {
                        width: '100%', height: '100%',
                        position: 'relative', overflow: 'visible',
                        '& .swiper-slide': { overflow: 'visible', height: '100%' }
                    },
                    '& .swiper-pagination': {
                        position: 'absolute', bgcolor: 'rgba(0,0,0,0.5)',
                        bottom: 0, left: 0, right: 0, pt: 1,
                        height: 108,
                        // pb: 'calc(80px + env(safe-area-inset-bottom, 0px))',
                        zIndex: 10,
                        '& .swiper-pagination-bullet': {
                            borderRadius: 3, transition: '180ms !important',
                            bgcolor: colors.grey[700],
                            opacity: 1,
                        },
                        '& .swiper-pagination-bullet-active': { bgcolor: colors.grey[50], opacity: 1 }
                    },
                }}>
                    <Swiper
                        dir="rtl"
                        slidesPerView={1}
                        modules={[Pagination]}
                        onSwiper={s => swiperRef.current = s}
                        pagination={{ enabled: true, clickable: true, type: 'bullets' }}
                        onSliderMove={() => { isSwipingRef.current = true; }}
                        onTouchEnd={() => { isSwipingRef.current = false; }}
                    >
                        {images?.map((img, index) => (
                            <SwiperSlide key={index}>
                                <GallerySlide
                                    img={img}
                                    swiperRef={swiperRef}
                                    onZoomChange={handleZoomChange}
                                    isSwipingRef={isSwipingRef}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Box>
            </Box>
        </Modal>
    );
}
